import { Head, Link } from "@inertiajs/react"
import { Building2, Users } from "lucide-react"

import { ChatInterface } from "@/components/chat-interface"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"

// Mock data
const orgData = {
  engineering: {
    name: "Engineering",
    description:
      "Software development, technical infrastructure, and internal platforms",
    teams: [
      {
        id: "product",
        name: "Product Engineering",
        description: "End-to-end feature development across web and mobile",
        employeeCount: 3,
        scenarioCount: 3,
      },
      {
        id: "platform",
        name: "Platform Team",
        description: "Internal tooling, CI/CD, and shared services",
        employeeCount: 2,
        scenarioCount: 3,
      },
      {
        id: "mobile",
        name: "Mobile Team",
        description: "Development of iOS and Android apps",
        employeeCount: 2,
        scenarioCount: 3,
      },
      {
        id: "qa",
        name: "Quality Assurance",
        description:
          "Manual and automated testing to ensure product reliability",
        employeeCount: 2,
        scenarioCount: 3,
      },
    ],
  },
  product: {
    name: "Product",
    description: "Product strategy, design, and cross-functional planning",
    teams: [
      {
        id: "design",
        name: "Design Team",
        description:
          "Visual and interaction design for web and mobile interfaces",
        employeeCount: 2,
        scenarioCount: 3,
      },
      {
        id: "product-mgmt",
        name: "Product Management",
        description: "Defines the roadmap and prioritizes product work",
        employeeCount: 1,
        scenarioCount: 2,
      },
    ],
  },
}

interface OrgShowProps {
  org_id: string
}

export default function OrganizationPage({ org_id }: OrgShowProps) {
  const org = orgData[org_id as keyof typeof orgData]

  if (!org) {
    return <div className="p-6">Organization not found</div>
  }

  return (
    <AppLayout>
      <Head title="Org" />

      <div className="p-6">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2">
            <Building2 className="text-primary size-6" />
            <h1 className="text-3xl font-bold tracking-tight">{org.name}</h1>
          </div>
          <p className="text-muted-foreground">{org.description}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h2 className="mb-4 text-xl font-semibold">Teams</h2>
            <div className="space-y-4 space-x-2">
              {org.teams.map((team) => (
                <Link href={`/orgs/${org_id}/teams/${team.id}`} key={team.id}>
                  <Card className="cursor-pointer transition-shadow hover:shadow-lg">
                    <CardHeader>
                      <div className="flex items-center">
                        <Users className="text-primary size-5" />
                        <CardTitle className="text-lg">{team.name}</CardTitle>
                      </div>
                      <CardDescription>{team.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4">
                        <Badge variant="secondary">
                          {team.employeeCount} employees
                        </Badge>
                        <Badge variant="outline">
                          {team.scenarioCount} scenarios
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-11 flex">
            <ChatInterface context={org.name} />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
